import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { ReportContext, UserContext } from '../../Context';
import axios from 'axios';
import DescriptionIcon from '@mui/icons-material/Description';
import { Document, Packer, Paragraph, TextRun, AlignmentType, Header, BorderStyle, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';
import { saveAs } from 'file-saver';

function GeneralAttestation() {
  const { report } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  
  const [loading, setLoading] = useState(true);
  const [personnesPhysiques, setPersonnesPhysiques] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [policierConnecte, setPolicierConnecte] = useState(null);
  
  const [personneSelectionnee, setPersonneSelectionnee] = useState('');
  const [personneSelectionneeData, setPersonneSelectionneeData] = useState(null);
  const [motifPresence, setMotifPresence] = useState('');
  const [dateHeureBasse, setDateHeureBasse] = useState('');
  const [dateHeureHaute, setDateHeureHaute] = useState('');

  // Helpers logo
  const svgTextToPngBase64 = (svgText, width = 150, height = 150) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve(base64);
      };
      
      img.onerror = reject;
      
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    });
  };

  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const loadLogoPng = async () => {
    const svgContent = `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150" width="150" height="150">
  <circle cx="75" cy="75" r="74" fill="#FFFFFF" stroke="#244BA6" stroke-width="2"/>
  <path fill="#244BA6" d="M150,75c0-41.4-33.6-75-75-75S0,33.6,0,75c0,39.5,30.5,71.9,69.3,74.8c-3.3-4-7.4-9.5-11-14.6
    c-6.1-8.6-13.4-21.5-13.7-34.4c-0.2-9.6,2-14.8,14.6-34.7c4.9-7.8,8.2-21.1,7.7-23.9c-0.5-2.9,4.6,4.3,6.5,9.1
    c2.5,6.4,2.2,11.2-2.5,24.4c-2.2,6.3-5.9,16.4-2.2,20.9c-0.4-0.5,11.9-13.2,14.8-26.2c2.2-10,1.3-23.3-3-33.1
    c-3.8-8.6-8.3-15.7-7.4-27.1c0.4-4.8,1.7-9.4,1.8-9.7c2.8,7,4,9.5,5.1,11.5c5.1,9.6,15.6,23.7,20.5,33.4
    c5,9.6,8.8,25.5,3,41.7c-5.7,15.9-11.1,21.3-16.9,34.2c-4.3,9.6-7.9,21.4-9.7,28.8C117.5,149,150,115.9,150,75"/>
</svg>`;
    
    try {
      const base64 = await svgTextToPngBase64(svgContent, 100, 100);
      return base64ToUint8Array(base64);
    } catch (error) {
      console.warn('Erreur conversion logo:', error);
      return null;
    }
  };
  
  // Fonction pour construire le numéro de PV complet
  const buildReportNumber = () => {
    if (!reportData || !settings) return 'XXXXXX';
    
    const shortJudicialDistrict = settings.shortJudicialDistrict || '__';
    const indice = reportData.indice || '__';
    const judicialDivision = settings.judicialDivision || '__';
    const number = report?.number || '__';
    const year = report?.year || '__';
    
    return `${shortJudicialDistrict}.${indice}.${judicialDivision}.${number}/${year}`;
  };
  
  // Fonction pour convertir les grades en texte complet
  const convertGrade = (gradeCode) => {
    const gradeMapping = {
      'AAGP': 'Aspirant Agent de Police',
      'AGP': 'Agent de Police',
      '1AGP': 'Premier Agent de Police',
      'ADAGP': 'Aspirant Agent de sécurisation de Police',
      'DAGP': 'Agent de sécurisation de Police',
      '1DAGP': 'Premier Agent de sécurisation de Police',
      'ABASP': 'Aspirant Assistant de Sécurisation de Police',
      'BASP': 'Assistant de Sécurisation de Police',
      '1BASP': 'Premier Assistant de Sécurisation de Police',
      'BCSP': 'Coordonnateur de Sécurisation de Police',
      '1BCSP': 'Premier Coordonnateur de Sécurisation de Police',
      'AINP': 'Aspirant Inspecteur de Police',
      'INP': 'Inspecteur de Police',
      '1INP': 'Premier Inspecteur de Police',
      'AINPP': 'Aspirant Inspecteur Principal de Police',
      'INPP': 'Inspecteur Principal de Police',
      '1INPP': 'Premier Inspecteur Principal de Police',
      'ACP': 'Aspirant Commissaire de Police',
      'CP': 'Commissaire de Police',
      '1CP': 'Premier Commissaire de Police',
      'CDP': 'Commissaire Divisionnaire de Police',
      '1CDP': 'Premier Commissaire Divisionnaire de Police'
    };
    return gradeMapping[gradeCode] || gradeCode;
  };

  // Fonction pour formater la date en français long
  const formatDateLongMonth = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const mois = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    const jour = String(date.getDate()).padStart(2, '0');
    const moisNom = mois[date.getMonth()];
    const annee = date.getFullYear();
    return `${jour} ${moisNom} ${annee}`;
  };

  // Fonction pour formater la date en JJ/MM/AAAA
  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const jour = String(date.getDate()).padStart(2, '0');
    const mois = String(date.getMonth() + 1).padStart(2, '0');
    const annee = date.getFullYear();
    return `${jour}/${mois}/${annee}`;
  };

  // Fonction pour extraire la date et l'heure d'un datetime-local
  const extractDateTimeParts = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const hourLabel = date.getHours() >= 2 ? 'heures' : 'heure';
    return {
      dateText: formatDateLongMonth(value),
      timeText: `${hours}:${minutes} ${hourLabel}`,
      dateObj: date
    };
  };

  // Fonction pour comparer si deux dates sont le même jour
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };
  
  // Charger les données au montage du composant
  useEffect(() => {
    const fetchData = async () => {
      if (!report || !report.number || !report.year) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        
        // Récupérer les paramètres
        const settingsResponse = await axios.get('/api/settings');
        setSettings(settingsResponse.data);
        
        // Récupérer les données du rapport
        const reportResponse = await axios.get(
          `/api/report/search?number=${report.number}&year=${report.year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setReportData(reportResponse.data);
        
        // Récupérer les infos du policier connecté via son matricule
        if (user) {
          try {
            const userResponse = await axios.get(
              `/api/user/avatar/${user}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const userData = userResponse.data;
            setPolicierConnecte({
              lastname: userData.lastname || '',
              firstname: userData.firstname || '',
              rank: convertGrade(userData.rank || '')
            });
          } catch (userError) {
            console.error('Erreur lors de la récupération des infos utilisateur:', userError);
          }
        }
        
        // Formater les personnes physiques (uniquement celles avec un nom)
        const personnesFormatted = (reportResponse.data.personnesPhysiques || [])
          .filter(p => p.lastname && !p.isUnknown)
          .map(p => ({
            value: `${p.lastname.toUpperCase()} ${p.firstname || ''}`,
            data: p
          }));
        
        setPersonnesPhysiques(personnesFormatted);
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [report, user]);
  
  // Gérer la sélection de la personne
  const handlePersonneChange = (e) => {
    const value = e.target.value;
    setPersonneSelectionnee(value);
    const selectedPersonne = personnesPhysiques.find(p => p.value === value);
    setPersonneSelectionneeData(selectedPersonne?.data || null);
  };
  
  // Vérifier si tous les champs sont remplis
  const isFormComplete = personneSelectionnee && motifPresence.trim() !== '' && dateHeureBasse && dateHeureHaute && policierConnecte;
  
  const handleGenerate = async () => {
    if (!isFormComplete || !reportData || !settings || !personneSelectionneeData || !policierConnecte) {
      console.error('Données manquantes pour générer le document');
      return;
    }
    
    try {
      const reportNumber = buildReportNumber();
      const logoImageData = await loadLogoPng();
      
      const fontFamily = 'Helvetica';
      const fontSize9pt = 18;
      const fontSize10pt = 20;
      const typeValue = settings.type === 'local' ? 'Locale' : settings.type === 'federal' ? 'Fédérale' : settings.type || 'N/A';
      
      const buildHeader = () => {
        const noBorders = {
          top: { style: BorderStyle.NONE },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
        };
        
        const headerRows = [
          new TableRow({
            children: [
              new TableCell({
                width: { size: 12, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    children: logoImageData ? [
                      new ImageRun({
                        data: logoImageData,
                        transformation: { width: 40, height: 40 },
                        type: 'png',
                      })
                    ] : []
                  })
                ]
              }),
              new TableCell({
                width: { size: 38, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `Police ${typeValue}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `${settings.clientName || 'N/A'} - ${settings.zoneNumber || 'N/A'}`,
                        font: fontFamily,
                        size: fontSize9pt,
                        bold: true,
                      }),
                    ],
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `Service ${reportData.unit || ''}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                ],
              }),
              new TableCell({
                width: { size: 50, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `Procès-verbal ${reportData.reportType === "subsequent" ? "subséquent" : reportData.reportType}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: reportNumber,
                        font: fontFamily,
                        size: fontSize9pt,
                        bold: true,
                      }),
                    ],
                  }),
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                      new TextRun({
                        text: '',
                        font: fontFamily,
                        size: fontSize9pt
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        ];
        
        const headerChildren = [
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
            rows: headerRows,
          }),
          new Paragraph({
            border: {
              bottom: {
                color: '000000',
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
            spacing: { after: 200, before: 0, line: 20 },
            children: [
              new TextRun({
                text: '',
                size: 4,
              }),
            ],
          })
        ];
        
        return new Header({ children: headerChildren });
      };
      
      // Construire l'identité complète de la personne
      const sexeFr = personneSelectionneeData.gender === 'M' ? 'Masculin' : 
                     personneSelectionneeData.gender === 'F' ? 'Féminin' : 
                     personneSelectionneeData.gender || '';
      
      const dateNaissance = personneSelectionneeData.birthdate ? 
        formatDateShort(personneSelectionneeData.birthdate) : '';
      
      const birthLine = dateNaissance ? `${dateNaissance}${personneSelectionneeData.birthPlace ? ` à ${personneSelectionneeData.birthPlace}` : ''}` : '';
      
      const buildNationalityLine = () => {
        const nat = personneSelectionneeData.nationality;
        const rrn = personneSelectionneeData.nationalNumber;
        
        if (!nat && !rrn) return null;
        
        if (nat && rrn) {
          return `${nat} (RRN : ${rrn})`;
        }
        if (nat) return nat;
        return `RRN : ${rrn}`;
      };

      const infoParagraph = (label, value, options = {}) => {
        const isBold = options.bold !== undefined ? options.bold : true;
        return value ? new Paragraph({
          children: [
            new TextRun({ text: `${label}`, font: fontFamily, size: fontSize10pt }),
            new TextRun({ 
              text: value, 
              font: fontFamily, 
              size: fontSize10pt, 
              bold: isBold,
              underline: options.underline ? {} : undefined
            })
          ]
        }) : null;
      };
      
      const identityParagraphs = [
        personneSelectionneeData ? new Paragraph({
          children: [
            new TextRun({ text: 'Nom et prénom : ', font: fontFamily, size: fontSize10pt }),
            new TextRun({ 
              text: (personneSelectionneeData.lastname || '').toUpperCase(), 
              font: fontFamily, 
              size: fontSize10pt, 
              bold: true,
              underline: {}
            }),
            new TextRun({ text: ' ', font: fontFamily, size: fontSize10pt }),
            new TextRun({ 
              text: personneSelectionneeData.firstname || '', 
              font: fontFamily, 
              size: fontSize10pt, 
              bold: true
            }),
            personneSelectionneeData.otherFirstnames ? new TextRun({ 
              text: ` ${personneSelectionneeData.otherFirstnames}`, 
              font: fontFamily, 
              size: fontSize10pt 
            }) : null
          ].filter(Boolean)
        }) : null,
        infoParagraph('Date et lieu de naissance : ', birthLine),
        infoParagraph('Sexe : ', sexeFr),
        infoParagraph('État civil : ', personneSelectionneeData.civilStatus || ''),
        buildNationalityLine() ? infoParagraph('Nationalité : ', buildNationalityLine()) : null,
        infoParagraph('Profession : ', personneSelectionneeData.profession || ''),
        infoParagraph('Domicile : ', personneSelectionneeData.residence || ''),
      ].filter(Boolean);

      // Calculer le texte de période de présence
      const dateBasseParts = extractDateTimeParts(dateHeureBasse);
      const dateHauteParts = extractDateTimeParts(dateHeureHaute);
      
      let periodeText = '';
      if (dateBasseParts && dateHauteParts) {
        if (isSameDay(dateHeureBasse, dateHeureHaute)) {
          // Même jour : n'indiquer que l'heure
          periodeText = `le ${dateBasseParts.dateText} de ${dateBasseParts.timeText} à ${dateHauteParts.timeText}`;
        } else {
          // Jours différents
          periodeText = `du ${dateBasseParts.dateText} à ${dateBasseParts.timeText} au ${dateHauteParts.dateText} à ${dateHauteParts.timeText}`;
        }
      }

      const isFemale = personneSelectionneeData.gender === 'F';
      
      // Créer le document
      const doc = new Document({
        sections: [{
          properties: {},
          headers: {
            default: buildHeader()
          },
          children: [
            // Titre dans un cadre
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE
              },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '000000' }
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({
                              text: 'ATTESTATION DE PRÉSENCE',
                              font: 'Helvetica',
                              size: 28,
                              bold: true
                            })
                          ]
                        })
                      ]
                    })
                  ]
                })
              ]
            }),
            
            // Espace
            new Paragraph({ text: '' }),
            new Paragraph({ text: '' }),
            
            // Corps du texte
            new Paragraph({
              children: [
                new TextRun({
                  text: `Nous soussigné `,
                  font: fontFamily,
                  size: fontSize10pt
                }),
                new TextRun({
                  text: (policierConnecte.lastname || '').toUpperCase(),
                  font: fontFamily,
                  size: fontSize10pt,
                  bold: true
                }),
                new TextRun({
                  text: ' ',
                  font: fontFamily,
                  size: fontSize10pt
                }),
                new TextRun({
                  text: policierConnecte.firstname || '',
                  font: fontFamily,
                  size: fontSize10pt,
                  bold: true
                }),
                new TextRun({
                  text: `, ${policierConnecte.rank}, attestons que ${isFemale ? 'la nommée' : 'le nommé'} :`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            // Identité complète
            ...identityParagraphs,
            
            new Paragraph({ text: '' }),
            
            // Texte de présence
            new Paragraph({
              children: [
                new TextRun({
                  text: `était ${isFemale ? 'présente' : 'présent'} en nos locaux ${periodeText} pour le motif suivant :`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            // Motif
            new Paragraph({
              children: [
                new TextRun({
                  text: motifPresence,
                  font: fontFamily,
                  size: fontSize10pt,
                  bold: true
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            new Paragraph({ text: '' }),
            new Paragraph({ text: '' }),
            
            // Signature du policier
            new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [
                new TextRun({
                  text: (policierConnecte.lastname || '').toUpperCase(),
                  font: fontFamily,
                  size: fontSize10pt,
                  bold: true
                }),
                new TextRun({
                  text: ', ',
                  font: fontFamily,
                  size: fontSize10pt
                }),
                new TextRun({
                  text: policierConnecte.firstname || '',
                  font: fontFamily,
                  size: fontSize10pt,
                  bold: true
                }),
                new TextRun({
                  text: `, ${policierConnecte.rank}`,
                  font: fontFamily,
                  size: fontSize10pt
                })
              ]
            })
          ]
        }]
      });
      
      // Générer et télécharger le fichier
      const blob = await Packer.toBlob(doc);
      const fileName = `${reportNumber}-attestation-presence.docx`;
      saveAs(blob, fileName);

      // Log du téléchargement
      try {
        const token = sessionStorage.getItem('authToken');
        
        await axios.post('/api/appendices/log-download', {
          appendixType: 'GeneralAttestation',
          appendixTitle: 'Attestation de présence',
          reportNumber: `${report?.number}/${report?.year}`,
          formData: {
            personnePhysique: `${personneSelectionneeData.lastname?.toUpperCase()} ${personneSelectionneeData.firstname}`,
            motifPresence: motifPresence,
            dateHeureDebut: dateHeureBasse,
            dateHeureFin: dateHeureHaute,
            policierSignataire: `${policierConnecte.rank} ${policierConnecte.lastname?.toUpperCase()} ${policierConnecte.firstname}`
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (logError) {
        console.warn('Erreur lors du log du téléchargement:', logError);
        // Ne pas bloquer le téléchargement en cas d'erreur de log
      }
      
    } catch (error) {
      console.error('Erreur lors de la génération du document:', error);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Complétez les informations ci-dessous pour générer l'attestation de présence.
        </Typography>
        
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Personne concernée */}
          <FormControl fullWidth required>
            <InputLabel id="personne-selectionnee-label">Personne physique</InputLabel>
            <Select
              labelId="personne-selectionnee-label"
              value={personneSelectionnee}
              onChange={handlePersonneChange}
              label="Personne physique"
            >
              {personnesPhysiques.map((personne, index) => (
                <MenuItem key={index} value={personne.value}>
                  {personne.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Motif de la présence */}
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Motif de la présence"
            value={motifPresence}
            onChange={(e) => setMotifPresence(e.target.value)}
          />
          
          {/* Date et heure basse de présence */}
          <TextField
            fullWidth
            required
            label="Date et heure de début de présence"
            type="datetime-local"
            value={dateHeureBasse}
            onChange={(e) => setDateHeureBasse(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
          />
          
          {/* Date et heure haute de présence */}
          <TextField
            fullWidth
            required
            label="Date et heure de fin de présence"
            type="datetime-local"
            value={dateHeureHaute}
            onChange={(e) => setDateHeureHaute(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
          />
          
          {/* Affichage du policier signataire */}
          {policierConnecte && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Signataire : {policierConnecte.rank} {policierConnecte.lastname?.toUpperCase()} {policierConnecte.firstname}
            </Typography>
          )}
          
          {/* Bouton de génération */}
          {isFormComplete && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={handleGenerate}
                sx={{
                  minWidth: 250,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Générer le document
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default GeneralAttestation;
